import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { Receipt, Bell, ArrowRight, Plus, X, Trash2, CheckCircle, Edit2, Save, FileText, ChevronDown, ChevronUp, Link as LinkIcon, TrendingDown } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';
import DocumentActionsBar from './DocumentActionsBar';
import { generateInvoicePdf, generateQuotePdf, QuotePdfData } from '../../../services/pdfService';

type InvoiceItem = { description: string; quantity: number; rate: number; total: number };

const InvoicingHub: React.FC = () => {
    const {
        invoices,
        quotes,
        products,
        catalogError,
        catalogSyncing,
        loadProducts,
        convertQuoteToInvoice,
        sendReminder,
        createInvoice,
        updateInvoiceStatus,
        updateInvoice,
        deleteInvoice,
        updateQuote,
        deleteQuote,
    } = useFinanceStore();

    const [showNewInvoice, setShowNewInvoice] = useState(false);
    const [newInvClientId, setNewInvClientId] = useState('');
    const [newInvClientName, setNewInvClientName] = useState('');
    const [newInvInvoiceNumber, setNewInvInvoiceNumber] = useState('');
    const [newInvDueDate, setNewInvDueDate] = useState('');
    const [newInvTaxRate, setNewInvTaxRate] = useState<number>(15);
    const [newInvTerms, setNewInvTerms] = useState('');
    const [newInvBanking, setNewInvBanking] = useState('');
    const [newInvItems, setNewInvItems] = useState<InvoiceItem[]>([{ description: '', quantity: 1, rate: 0, total: 0 }]);
    const [newInvQuoteId, setNewInvQuoteId] = useState<string | undefined>(undefined);

    const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
    const [editData, setEditData] = useState<Partial<typeof invoices[0]>>({});
    const [editInvItems, setEditInvItems] = useState<InvoiceItem[]>([]);
    const [expandedLinkedQuote, setExpandedLinkedQuote] = useState<string | null>(null);
    const [invoicePdfMap, setInvoicePdfMap] = useState<Record<string, Blob>>({});
    const [quotePdfMap, setQuotePdfMap] = useState<Record<string, Blob>>({});
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const resetNewInvoiceForm = () => {
        setNewInvClientId('');
        setNewInvClientName('');
        setNewInvInvoiceNumber('');
        setNewInvDueDate('');
        setNewInvTaxRate(15);
        setNewInvTerms('');
        setNewInvBanking('');
        setNewInvItems([{ description: '', quantity: 1, rate: 0, total: 0 }]);
        setNewInvQuoteId(undefined);
    };

    const newInvSubtotal = newInvItems.reduce((s, it) => s + (it.total || it.quantity * it.rate), 0);
    const newInvTax = newInvSubtotal * (newInvTaxRate / 100);
    const newInvTotal = newInvSubtotal + newInvTax;

    const handleCreateInvoice = async () => {
        if (!newInvClientId || newInvItems.length === 0 || newInvTotal <= 0) { return; }
        const cleanItems = newInvItems
            .filter(i => i.description.trim() !== '')
            .map(it => ({ ...it, total: it.quantity * it.rate }));
        if (cleanItems.length === 0) { return; }
        await createInvoice({
            clientId: newInvClientId,
            clientName: newInvClientName,
            invoiceNumber: newInvInvoiceNumber || undefined,
            amount: newInvTotal,
            subtotal: newInvSubtotal,
            items: cleanItems,
            taxRate: newInvTaxRate,
            totalTaxes: newInvTax,
            terms: newInvTerms,
            banking: newInvBanking,
            dueDate: newInvDueDate || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
            balance: newInvTotal,
            paidToDate: 0,
            status: 'unpaid',
            quoteId: newInvQuoteId,
        });
        if (newInvQuoteId) {
            await updateQuote(newInvQuoteId, { status: 'invoiced' as any });
        }
        setShowNewInvoice(false);
        resetNewInvoiceForm();
    };

    const handleStartEdit = (invoice: typeof invoices[0]) => {
        setEditingInvoiceId(invoice.id);
        setEditData({ ...invoice });
        setEditInvItems(invoice.items && invoice.items.length > 0 ? invoice.items.map(i => ({ ...i })) : [{ description: '', quantity: 1, rate: 0, total: 0 }]);
    };

    const handleSaveEdit = async (id: string) => {
        const recalculatedItems = editInvItems.map(it => ({ ...it, total: it.quantity * it.rate }));
        const recalculatedSubtotal = recalculatedItems.reduce((s, it) => s + it.total, 0);
        const taxRate = editData.taxRate ?? 15;
        const recalculatedTax = recalculatedSubtotal * (taxRate / 100);
        const recalculatedTotal = recalculatedSubtotal + recalculatedTax;
        await updateInvoice(id, {
            ...editData,
            items: recalculatedItems,
            subtotal: recalculatedSubtotal,
            totalTaxes: recalculatedTax,
            amount: recalculatedTotal,
            balance: recalculatedTotal - (editData.paidToDate ?? 0),
        });
        setEditingInvoiceId(null);
        setEditData({});
        setEditInvItems([]);
    };

    const handleNewInvItemChange = (idx: number, field: keyof InvoiceItem, value: string | number) => {
        const next = [...newInvItems];
        const item: InvoiceItem = { ...next[idx] };
        if (field === 'quantity' || field === 'rate') {
            (item[field] as number) = Number(value);
        } else {
            (item as any)[field] = value;
        }
        item.total = item.quantity * item.rate;
        next[idx] = item;
        setNewInvItems(next);
    };

    const handleNewInvAddItem = () => {
        setNewInvItems([...newInvItems, { description: '', quantity: 1, rate: 0, total: 0 }]);
    };

    const handleNewInvRemoveItem = (idx: number) => {
        setNewInvItems(newInvItems.filter((_, i) => i !== idx));
    };

    const handleNewInvAddProduct = (productId: string) => {
        const product = products.find(p => p.id === productId);
        if (!product) { return; }
        const existing = newInvItems.find(it => it.description === product.name);
        if (existing) {
            const next = newInvItems.map(it =>
                it.description === product.name
                    ? { ...it, quantity: it.quantity + 1, total: (it.quantity + 1) * it.rate }
                    : it
            );
            setNewInvItems(next);
        } else {
            setNewInvItems([...newInvItems, { description: product.name, quantity: 1, rate: product.price, total: product.price }]);
        }
    };

    const handleEditInvItemChange = (idx: number, field: keyof InvoiceItem, value: string | number) => {
        const next = [...editInvItems];
        const item: InvoiceItem = { ...next[idx] };
        if (field === 'quantity' || field === 'rate') {
            (item[field] as number) = Number(value);
        } else {
            (item as any)[field] = value;
        }
        item.total = item.quantity * item.rate;
        next[idx] = item;
        setEditInvItems(next);
    };

    const handleEditInvAddItem = () => {
        setEditInvItems([...editInvItems, { description: '', quantity: 1, rate: 0, total: 0 }]);
    };

    const handleEditInvRemoveItem = (idx: number) => {
        setEditInvItems(editInvItems.filter((_, i) => i !== idx));
    };

    const handleDeleteQuote = async (quoteId: string) => {
        if (!confirm('Delete this quote? It will be removed from Ready for Invoicing.')) { return; }
        await deleteQuote(quoteId);
    };

    const approvedQuotes = quotes.filter(q => q.status === 'approved');

    const handleGenerateInvoicePdf = async (id: string) => {
        const invoice = invoices.find(i => i.id === id);
        if (!invoice) { return new Blob(); }
        setIsGeneratingPdf(true);
        try {
            const pdf = await generateInvoicePdf(invoice);
            setInvoicePdfMap(prev => ({ ...prev, [id]: pdf }));
            return pdf;
        } catch (error) {
            console.error('Error generating invoice PDF:', error);
            return new Blob();
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleGenerateQuotePdf = async (id: string) => {
        const quote = quotes.find(q => q.id === id);
        if (!quote) { return new Blob(); }
        setIsGeneratingPdf(true);
        try {
            const pdf = await generateQuotePdf({
                ...quote,
                items: quote.items.map(it => ({ description: it.description ?? '', quantity: it.quantity, rate: it.rate, total: it.total }))
            } as QuotePdfData);
            setQuotePdfMap(prev => ({ ...prev, [id]: pdf }));
            return pdf;
        } catch (error) {
            console.error('Error generating quote PDF:', error);
            return new Blob();
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Invoicing</h3>
                <div className="flex gap-2">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                        {invoices.filter(i => i.status === 'unpaid').length} Unpaid
                    </span>
                    <button
                        onClick={() => setShowNewInvoice(true)}
                        className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95"
                    >
                        <Plus size={12} /> New Invoice
                    </button>
                </div>
            </div>

            {showNewInvoice && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-black text-blue-800 uppercase tracking-widest">Create New Invoice</div>
                        <button onClick={() => { setShowNewInvoice(false); resetNewInvoiceForm(); }} className="text-slate-400 hover:text-slate-600">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input type="text" placeholder="Client ID *" value={newInvClientId} onChange={e => setNewInvClientId(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="text" placeholder="Client Name" value={newInvClientName} onChange={e => setNewInvClientName(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="text" placeholder="Invoice Number (optional)" value={newInvInvoiceNumber} onChange={e => setNewInvInvoiceNumber(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="date" placeholder="Due Date" value={newInvDueDate} onChange={e => setNewInvDueDate(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="number" placeholder="Tax Rate (%)" value={newInvTaxRate} onChange={e => setNewInvTaxRate(Number(e.target.value))}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Terms & Conditions</label>
                        <textarea value={newInvTerms} onChange={e => setNewInvTerms(e.target.value)} rows={2}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Banking Details</label>
                        <textarea value={newInvBanking} onChange={e => setNewInvBanking(e.target.value)} rows={2}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Line Items</label>
                        {newInvItems.map((item, idx) => (
                            <div key={`new-inv-item-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded-xl border border-slate-200">
                                <div className="col-span-5">
                                    <input type="text" placeholder="Description" value={item.description} onChange={e => handleNewInvItemChange(idx, 'description', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                </div>
                                <div className="col-span-2">
                                    <input type="number" placeholder="Qty" value={item.quantity} onChange={e => handleNewInvItemChange(idx, 'quantity', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                </div>
                                <div className="col-span-2">
                                    <input type="number" placeholder="Rate" value={item.rate} onChange={e => handleNewInvItemChange(idx, 'rate', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                </div>
                                <div className="col-span-2 text-sm font-bold text-slate-700 text-right">{formatZAR(item.total)}</div>
                                <div className="col-span-1">
                                    <button onClick={() => handleNewInvRemoveItem(idx)} className="text-slate-400 hover:text-red-500">
                                        <X size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <div className="flex flex-wrap gap-2 items-center">
                            <button onClick={handleNewInvAddItem} className="text-xs text-blue-600 font-bold hover:underline">+ Add Line Item</button>
                            {products.length > 0 && (
                                <div className="flex-1 min-w-[180px]">
                                    <select
                                        onChange={e => { handleNewInvAddProduct(e.target.value); e.target.value = ''; }}
                                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                                        defaultValue=""
                                    >
                                        <option value="" disabled>+ Add from Catalog</option>
                                        {products.filter(p => p.status !== 'inactive').map(p => (
                                            <option key={p.id} value={p.id}>{p.name} — R {p.price.toFixed(2)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {catalogError && (
                                <div className="text-xs text-red-600 mb-1">{catalogError}</div>
                            )}
                            {catalogError && (
                                <button onClick={loadProducts} className="mt-1 text-blue-600 underline text-xs">
                                    Retry
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 pt-2 border-t border-blue-200">
                        <div className="text-xs text-slate-500 space-y-0.5">
                            <div>Subtotal: <span className="font-semibold text-slate-700">{formatZAR(newInvSubtotal)}</span></div>
                            <div>Tax ({newInvTaxRate}%): <span className="font-semibold text-slate-700">{formatZAR(newInvTax)}</span></div>
                            <div className="font-bold text-slate-800">Total: {formatZAR(newInvTotal)}</div>
                        </div>
                        <button
                            onClick={handleCreateInvoice}
                            disabled={!newInvClientId || newInvTotal <= 0 || newInvItems.filter(i => i.description.trim() !== '').length === 0}
                            className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Create Invoice
                        </button>
                    </div>
                </div>
            )}

            <div className="grid gap-6">
                <div className="space-y-3">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <FileText size={12} /> Ready for Invoicing
                    </div>
                    <div className="grid gap-3">
                        {approvedQuotes.length === 0 && (
                            <div className="text-xs text-slate-400 italic py-2">No approved quotes ready for invoicing</div>
                        )}
                        {approvedQuotes.map((quote, idx) => {
                            const linkedInvoice = invoices.find(i => i.quoteId === quote.id || i.quoteId === quote.quoteNumber);
                            return (
                                <div key={quote.id || `approved-quote-${idx}`} className="p-3 bg-white rounded-xl border border-slate-200 group hover:border-blue-300 transition-all">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-slate-700">
                                            <span className="font-semibold">{quote.quoteNumber || quote.id}</span>
                                            {quote.clientName && <span className="text-slate-400"> • {quote.clientName}</span>}
                                            <span className="text-slate-400"> — {formatZAR(quote.total)}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setExpandedLinkedQuote(expandedLinkedQuote === quote.id ? null : quote.id)}
                                                className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors"
                                                title="Expand"
                                            >
                                                {expandedLinkedQuote === quote.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteQuote(quote.id)}
                                                className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                                                title="Delete Quote"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            {linkedInvoice ? (
                                                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-wider" title={`Linked to Invoice ${linkedInvoice.invoiceNumber || linkedInvoice.id}`}>
                                                    <LinkIcon size={12} /> Linked: {linkedInvoice.invoiceNumber || linkedInvoice.id}
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => convertQuoteToInvoice(quote.id)}
                                                    className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-blue-600 hover:text-blue-700 transition-colors"
                                                >
                                                    Invoice Now <ArrowRight size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {expandedLinkedQuote === quote.id && (
                                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Quote Status</label>
                                                    <select
                                                        value={quote.status}
                                                        onChange={e => updateQuote(quote.id, { status: e.target.value as any })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                                                    >
                                                        <option value="draft">Draft</option>
                                                        <option value="sent">Sent</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="invoiced">Invoiced</option>
                                                        <option value="cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Client Name</label>
                                                    <input
                                                        type="text"
                                                        value={quote.clientName || ''}
                                                        onChange={e => updateQuote(quote.id, { clientName: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Total</label>
                                                    <input
                                                        type="number"
                                                        value={quote.total || 0}
                                                        onChange={e => updateQuote(quote.id, { total: Number(e.target.value) })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                                                    />
                                                </div>
                                            </div>
                                            {quote.items && quote.items.length > 0 && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Line Items</div>
                                                    {quote.items.map((item, i) => (
                                                        <div key={`rfq-item-${i}`} className="flex justify-between text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                                                            <span>{item.description || `Item ${i + 1}`} <span className="text-slate-400">× {item.quantity} @ {formatZAR(item.rate)}</span></span>
                                                            <span className="font-semibold">{formatZAR(item.total)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {quote.id && (
                                        <DocumentActionsBar
                                            kind="quote"
                                            reference={quote.id}
                                            pdf={quotePdfMap[quote.id] || new Blob()}
                                            clientName={quote.clientName}
                                            documentLabel={`Quote ${quote.quoteNumber || quote.id}`}
                                            onAfterAction={() => handleGenerateQuotePdf(quote.id)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <Receipt size={12} /> Active Invoices
                    </div>
                    <div className="gap-3">
                        {invoices.map((invoice, idx) => (
                            <React.Fragment key={invoice.id || `invoice-${idx}`}>
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden group hover:border-blue-300 transition-all">
                                {editingInvoiceId === invoice.id ? (
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Invoice Number</label>
                                                <input type="text" value={editData.invoiceNumber || ''} onChange={e => setEditData({ ...editData, invoiceNumber: e.target.value })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Client Name</label>
                                                <input type="text" value={editData.clientName || ''} onChange={e => setEditData({ ...editData, clientName: e.target.value })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Paid To Date</label>
                                                <input type="number" value={editData.paidToDate || 0} onChange={e => setEditData({ ...editData, paidToDate: Number(e.target.value) })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Tax Rate (%)</label>
                                                <input type="number" value={editData.taxRate || 0} onChange={e => setEditData({ ...editData, taxRate: Number(e.target.value) })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Due Date</label>
                                                <input type="date" value={editData.dueDate || ''} onChange={e => setEditData({ ...editData, dueDate: e.target.value })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</label>
                                                <select value={editData.status || 'unpaid'} onChange={e => setEditData({ ...editData, status: e.target.value as any })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white">
                                                    <option value="draft">Draft</option>
                                                    <option value="sent">Sent</option>
                                                    <option value="unpaid">Unpaid</option>
                                                    <option value="partial">Partial</option>
                                                    <option value="paid">Paid</option>
                                                    <option value="overdue">Overdue</option>
                                                    <option value="cancelled">Cancelled</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Line Items</label>
                                            {editInvItems.map((item, i) => (
                                                <div key={`edit-inv-item-${i}`} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded-xl border border-slate-200">
                                                    <div className="col-span-5">
                                                        <input type="text" placeholder="Description" value={item.description} onChange={e => handleEditInvItemChange(i, 'description', e.target.value)}
                                                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <input type="number" placeholder="Qty" value={item.quantity} onChange={e => handleEditInvItemChange(i, 'quantity', e.target.value)}
                                                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <input type="number" placeholder="Rate" value={item.rate} onChange={e => handleEditInvItemChange(i, 'rate', e.target.value)}
                                                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                                    </div>
                                                    <div className="col-span-2 text-sm font-bold text-slate-700 text-right">{formatZAR(item.total)}</div>
                                                    <div className="col-span-1">
                                                        <button onClick={() => handleEditInvRemoveItem(i)} className="text-slate-400 hover:text-red-500">
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex flex-wrap gap-2 items-center">
                                                <button onClick={handleEditInvAddItem} className="text-xs text-blue-600 font-bold hover:underline">+ Add Line Item</button>
                                                {products.length > 0 && (
                                                    <div className="flex-1 min-w-[180px]">
                                                        <select
                                                            onChange={e => {
                                                                const product = products.find(p => p.id === e.target.value);
                                                                if (product) {
                                                                    setEditInvItems([...editInvItems, { description: product.name, quantity: 1, rate: product.price, total: product.price }]);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                                                            defaultValue=""
                                                        >
                                                            <option value="" disabled>+ Add from Catalog</option>
                                                            {products.filter(p => p.status !== 'inactive').map(p => (
                                                                <option key={p.id} value={p.id}>{p.name} — R {p.price.toFixed(2)}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Terms & Conditions</label>
                                            <textarea value={editData.terms || ''} onChange={e => setEditData({ ...editData, terms: e.target.value })} rows={2}
                                                className="w-full px-2 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Banking Details</label>
                                            <textarea value={editData.banking || ''} onChange={e => setEditData({ ...editData, banking: e.target.value })} rows={2}
                                                className="w-full px-2 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleSaveEdit(invoice.id)} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-bold">
                                                <Save size={12} /> Save
                                            </button>
                                            <button onClick={() => { setEditingInvoiceId(null); setEditData({}); setEditInvItems([]); }} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-lg text-xs font-bold">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="p-4 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-xl ${invoice.status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                                    <Receipt size={20} />
                                                </div>
                                                <div className="text-sm">
                                                    <div className="font-bold text-slate-800">
                                                        {formatZAR(invoice.amount)}
                                                    </div>
                                                    {invoice.balance > 0 && invoice.status !== 'paid' && (
                                                        <span className="text-amber-600 font-normal text-xs ml-2">(bal: {formatZAR(invoice.balance)})</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                {invoice.status !== 'paid' && (
                                                    <button onClick={() => updateInvoiceStatus(invoice.id, 'paid')} className="p-2 text-slate-400 hover:text-green-600 transition-colors" title="Mark as Paid">
                                                        <CheckCircle size={16} />
                                                    </button>
                                                )}
                                                {invoice.status !== 'paid' && (
                                                    <button onClick={() => sendReminder(invoice.id)} className="p-2 text-slate-400 hover:text-amber-600 transition-colors" title="Send Reminder">
                                                        <Bell size={16} />
                                                    </button>
                                                )}
                                                <button onClick={() => handleStartEdit(invoice)} className="p-2 text-slate-400 hover:text-blue-600 transition-colors" title="Edit">
                                                    <Edit2 size={16} />
                                                </button>
                                                <button onClick={() => deleteInvoice(invoice.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Delete">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        {invoice.id && (
                                            <div className="px-4 pb-3 border-t border-slate-100 pt-2">
                                                <DocumentActionsBar
                                                    kind="invoice"
                                                    reference={invoice.id}
                                                    pdf={invoicePdfMap[invoice.id] || new Blob()}
                                                    clientName={invoice.clientName}
                                                    documentLabel={`Invoice ${invoice.invoiceNumber || invoice.id}`}
                                                    compact
                                                    onAfterAction={() => handleGenerateInvoicePdf(invoice.id)}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoicingHub;